/*
 * Dark mode example — override :root vars:
 *
 * :root {
 *   --ds-bg: #1e1e1e;
 *   --ds-bg-sync: #2a2520;
 *   --ds-fg: #d4d4d4;
 *   --ds-fg-sync: #c8c0b0;
 *   --ds-td-border-color: #444;
 *   --ds-scrollbar-thumb: rgba(255, 255, 255, 0.25);
 *   --ds-scrollbar-border: rgba(255, 255, 255, 0.1);
 *   --ds-stripe-color: hsl(0 0% 30% / 0.95);
 *   --ds-stripe-boost-color: hsl(0 0% 30% / 0.4);
 *   --ds-color-red: #ff6b6b;
 * }
 */

/* ===== Customizable variables (override via :root) ===== */
:root {
	--ds-base-font-size: 14px;
	--ds-line-height: 1.6;
	--ds-content-padding: 8px;
	--ds-td-padding: 4px;
	--ds-td-border-color: #666666;
	--ds-underline-offset: 0.2em;
	--ds-bg: #ffffff;
	--ds-bg-sync: #f5f0e6;
	--ds-fg: #000000;
	--ds-fg-sync: #2f2a24;
	--ds-minimap-width: 15px;
	--ds-font-family: inherit;
	--ds-scrollbar-thumb: rgba(0, 0, 0, 0.4);
	--ds-scrollbar-border: rgba(128, 128, 128, 0.2);
	--ds-stripe-color: hsl(0 0% 88% / 0.95);
	--ds-stripe-boost-color: hsl(0 0% 88% / 0.4);
	--ds-color-red: red;
}

/* ===== Workspace ===== */

.ds-workspace {
	--_ds-bg: var(--ds-bg);
	--_ds-fg: var(--ds-fg);
	position: relative;
	isolation: isolate;
	min-width: 0;
	height: 100%;
	background-color: var(--_ds-bg);
}

.ds-workspace.ds-sync-mode {
	--_ds-bg: var(--ds-bg-sync);
	--_ds-fg: var(--ds-fg-sync);
}

/* ===== Deck / Editor / Renderer layout ===== */

.ds-deck {
	z-index: 1;
	min-height: 0;
	height: 100%;
	position: relative;
	display: grid;
	grid-template-areas: "left right minimap";
	grid-template-columns: 1fr 1fr auto;
	background-color: transparent;
}

.ds-editor {
	position: relative;
	min-width: 0;
	height: 100%;
	overflow-x: hidden;
	overflow-y: scroll;
	display: flex;
	flex-direction: column;
	background-color: transparent;
	color: var(--_ds-fg);
	line-height: var(--ds-line-height);
	contain: layout size;
}

.ds-editor-left {
	grid-area: left;
}

.ds-editor-right {
	grid-area: right;
}

.ds-minimap {
	display: none;
	grid-area: minimap;
}

.ds-renderer {
	z-index: 0;
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
}

.ds-renderer--suspended {
	opacity: 0.5;
}

.ds-diff-layer {
	z-index: 0;
}

.ds-highlight-layer {
	z-index: 1;
}

.ds-renderer canvas {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	will-change: transform;
}

/* ===== Gutter frame =====
 * 미니맵/스크롤바 거터의 좌우 경계선(= 좌우 에디터 사이 경계선).
 * ::-webkit-scrollbar의 border로 그리면 스크롤바 스타일 오버라이드를
 * 지원하지 않는 환경(iOS Safari 등 오버레이 스크롤바)에서 통째로 사라지므로,
 * deck 위에 별도 오버레이로 그린다. grid-area로 각 에디터 영역에 배치.
 */
.ds-deck::before,
.ds-deck::after {
	content: "";
	position: absolute;
	top: 0;
	bottom: 0;
	right: 0;
	width: var(--ds-minimap-width, 15px);
	box-sizing: border-box;
	border-inline: 1px solid var(--ds-scrollbar-border);
	pointer-events: none;
	z-index: 2;
}

.ds-deck::before {
	grid-area: left;
}

.ds-deck::after {
	grid-area: right;
}

/* ===== Scrollbar ===== */

.ds-editor::-webkit-scrollbar-button {
	display: none;
}

.ds-editor::-webkit-scrollbar {
	width: var(--ds-minimap-width, 15px);
}

/* 투명 border + content-box clip으로 gutter frame 경계선을 침범하지 않게 한다. */
.ds-editor::-webkit-scrollbar-thumb {
	background-color: var(--ds-scrollbar-thumb);
	border-radius: 0px;
	border-inline: 1px solid transparent;
	background-clip: content-box;
}

@supports not selector(::-webkit-scrollbar) {
	.ds-editor {
		scrollbar-color: var(--ds-scrollbar-thumb) transparent;
	}
}

/* ===== Editor content ===== */

.ds-editor-content {
	flex: 1 0 auto;
	position: relative;
	border: 0;
	min-width: 0;
	max-width: 100%;
	min-height: 100%;
	overflow: hidden;
	font-size: var(--ds-base-font-size);
	padding: var(--ds-content-padding);
	scroll-padding-top: calc(3em * var(--ds-line-height));
	word-break: keep-all;
	overflow-wrap: anywhere;
	outline: none;
	background-color: transparent;
	font-family: var(--ds-font-family);
}

/* 오버레이 스크롤바 환경에서는 스크롤바가 레이아웃 폭을 차지하지 않아
 * 미니맵 자리가 확보되지 않는다. 본문 padding으로 직접 확보한다.
 * (ds-overlay-scrollbar 클래스는 엔진이 실측해서 토글) */
.ds-overlay-scrollbar .ds-editor-content {
	padding-inline-end: calc(var(--ds-content-padding) + var(--ds-minimap-width, 15px));
}

.ds-editor-content * {
	line-height: var(--ds-line-height);
	max-width: 100%;
}

.ds-editor-content p {
	margin: 0 0 0.5em 0;
}

.ds-editor-content pre {
	margin: 0;
	font-family: inherit;
	font-size: inherit;
	line-height: inherit;
	white-space: pre-wrap;
	word-break: break-all;
}

.ds-editor-content code {
	font-family: monospace;
}

.ds-editor-content u {
	text-underline-offset: var(--ds-underline-offset);
}

.ds-editor-content table {
	table-layout: auto;
	border-collapse: collapse;
	border-spacing: 0;
	width: 100%;
}

.ds-editor-content td,
.ds-editor-content th {
	position: relative;
}

.ds-editor-content td {
	vertical-align: top;
	border: 1px solid var(--ds-td-border-color);
	padding: var(--ds-td-padding);
	min-height: calc(1em * var(--ds-line-height));
}

/* ===== Color markers (sanitize) ===== */

.ds-editor-content .ds-color-red {
	color: var(--ds-color-red);
}

.ds-editor-content .ds-color-normal {
	color: var(--_ds-fg);
}

/* ===== Minimap ===== */

.ds-editor-minimap {
	position: absolute;
	pointer-events: none;
	top: 0;
	right: calc(var(--ds-minimap-width, 15px) * -1);
	bottom: 0;
	width: var(--ds-minimap-width, 15px);
}

/* ===== ds-diff (custom element) ===== */

ds-diff {
	visibility: hidden;
	display: inline-block;
	height: 0;
	user-select: none;
	width: 0px;
}

/* TODO: 용도 확인 필요 — 아래에서 width: 0px로 덮어쓰여짐 */
/* .ds-diff { width: 4px; } */

/* ===== ds-anchor (custom element) ===== */

ds-anchor {
	display: none;
}

/* ===== Sync mode ===== */

.ds-sync-mode .ds-editor-content {
	min-height: unset;
}

.ds-sync-mode ds-diff {
	display: inline-block;
	position: relative;
}

.ds-sync-mode ds-diff.ds-has-preceding-space::before {
	content: " ";
	display: inline;
}

.ds-sync-mode ds-diff.ds-has-following-space::after {
	content: " ";
	display: inline;
}

.ds-sync-mode ds-diff.ds-line-start {
	display: block !important;
	height: calc(var(--ds-line-height) * 1em) !important;
	width: 2px;
}

.ds-sync-mode ds-diff.ds-line-start::before {
	content: "" !important;
	display: block;
}

.ds-sync-mode ds-diff.ds-line-end::after {
	content: "" !important;
	display: block !important;
}

.ds-sync-mode ds-anchor[data-anchor-index] {
	display: block;
}

.ds-sync-mode .ds-padded::before {
	content: "" !important;
	display: block !important;
	height: var(--ds-adjust, 0px) !important;
}

.ds-sync-mode .ds-striped.ds-padded::before {
	background: transparent
		repeating-linear-gradient(
			135deg,
			var(--ds-stripe-color) 0px,
			var(--ds-stripe-color) 3px,
			transparent 3px,
			transparent 6px
		);
	opacity: 0.75;
}

/* ===== Height boost (sync mode) ===== */

.ds-editor-height-boost {
	display: none;
}

.ds-sync-mode .ds-editor-height-boost {
	display: block;
	flex: 0 0 auto;
	background: repeating-linear-gradient(
		0deg,
		var(--ds-stripe-boost-color) 0px,
		var(--ds-stripe-boost-color) 3px,
		transparent 3px,
		transparent 6px
	);
	height: 0;
	/* height: var(--ds-height-boost, 0px); */
}
